import { CesiumManager } from './cesium/cesiumManager.js';
import { AircraftManager3D } from './cesium/aircraftManager3D.js';
import { TrailManager3D } from './cesium/trailManager3D.js';
import { TaskManager3D } from './cesium/taskManager3D.js';

/**
 * Manages switching between 2D (Leaflet) and 3D (Cesium) views
 */
export class MapManager {
  constructor(leafletMap, aircraftManager2D, cesiumIonToken) {
    this.mode = '2d'; // '2d' | '3d'
    
    // 2D components
    this.leafletMap = leafletMap;
    this.aircraftManager2D = aircraftManager2D;
    
    // 3D components (lazy loaded)
    this.cesiumManager = null;
    this.aircraftManager3D = null;
    this.trailManager3D = null;
    this.taskManager3D = null;
    this.cesiumIonToken = cesiumIonToken;
    
    // Shared state
    this.aircraftData = new Map(); // cookie -> { position, trail, baseColor }
    this.config = {
      trailLength: 100,
      infoLabel: 'altitude',
      colorScheme: 'altitude',
      colorSensitivity: 0,
      trendColorScheme: 'gain_loss',
      units: 'imperial',
      teGain: 1.0,
      teAccelLimit: 3.0,
      cnLabelSize: 16
    };
    
    // DOM elements
    this.mapContainer = document.getElementById('map');
    this.cesiumContainer = document.getElementById('cesium-container');
  }
  
  /** Simple mobile detection (screen + touch). */
  isMobile() {
    const mq = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    const touch = (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) || 'ontouchstart' in window;
    const ua = /Mobi|Android|iPad|iPhone|iPod/i.test(navigator.userAgent || '');
    return !!(mq || (touch && ua));
  }
  
  /**
   * Initialize 3D view (lazy loading)
   */
  async init3D() {
    if (this.cesiumManager) return; // Already initialized
    
    console.log('🌍 Initializing 3D view...');
    
    try {
      // Create Cesium manager
      this.cesiumManager = new CesiumManager('cesium-container', this.cesiumIonToken);
      
      // Create aircraft, trail, and task managers
      this.aircraftManager3D = new AircraftManager3D(this.cesiumManager.viewer);
      this.trailManager3D = new TrailManager3D(this.cesiumManager.viewer, this.aircraftManager3D);
      this.taskManager3D = new TaskManager3D(this.cesiumManager.viewer);
      
      // Apply current config
      this.aircraftManager3D.setColorScheme(this.config.colorScheme);
      this.aircraftManager3D.setColorSensitivity(this.config.colorSensitivity);
      this.aircraftManager3D.setTrendColorScheme(this.config.trendColorScheme);
      this.aircraftManager3D.setInfoLabel(this.config.infoLabel);
      this.aircraftManager3D.setUnits(this.config.units);
      this.aircraftManager3D.setTEParams(this.config.teGain, this.config.teAccelLimit);
      this.trailManager3D.setMaxPoints(this.config.trailLength);
      
      // Apply label size if configured
      if (this.config.cnLabelSize) {
        this.aircraftManager3D.setCNLabelSize(this.config.cnLabelSize);
      }
      
      // Set follow callback
      this.aircraftManager3D.setFollowCallback((callsign) => {
        // Sync with 2D follow callback
        if (this.aircraftManager2D.followCallback) {
          this.aircraftManager2D.followCallback(callsign);
        }
      });
      
      console.log('✅ 3D view initialized');
    } catch (error) {
      console.error('❌ Failed to initialize 3D view:', error);
      throw error;
    }
  }
  
  /**
   * Switch to 3D mode
   */
  async switchTo3D() {
    if (this.mode === '3d') return;
    // Disable 3D on mobile devices
    if (this.isMobile()) {
      console.log('🚫 3D mode disabled on mobile. Staying in 2D.');
      return;
    }
    
    console.log('🔄 Switching to 3D mode...');
    
    // Initialize 3D if needed
    if (!this.cesiumManager) {
      await this.init3D();
    }
    
    // Get current 2D camera position
    const center = this.leafletMap.getCenter();
    const zoom = this.leafletMap.getZoom();
    
    // Calculate approximate height from zoom level
    const height = this.zoomToHeight(zoom);
    
    // Hide 2D, show 3D
    this.mapContainer.style.display = 'none';
    this.cesiumContainer.style.display = 'block';
    
    // Resume 3D rendering and resize after container is visible
    this.cesiumManager.resumeRendering();
    setTimeout(() => {
      if (this.cesiumManager && this.cesiumManager.viewer) {
        this.cesiumManager.viewer.resize();
        this.cesiumManager.viewer.scene.requestRender();
      }
    }, 100);
    
    // Set 3D camera to match 2D view
    this.cesiumManager.setCameraView(center.lng, center.lat, height);
    
    // Transfer aircraft state to 3D
    this.syncAircraftTo3D();
    
    this.mode = '3d';
    console.log('✅ Switched to 3D mode');
  }
  
  /**
   * Switch to 2D mode
   */
  switchTo2D() {
    if (this.mode === '2d') return;
    
    console.log('🔄 Switching to 2D mode...');
    
    // Get current 3D camera position
    const cameraPos = this.cesiumManager.getCameraPosition();
    
    // Calculate zoom from height
    const zoom = this.heightToZoom(cameraPos.height);
    
    // Hide 3D, show 2D — pause 3D rendering to free GPU
    this.cesiumContainer.style.display = 'none';
    this.mapContainer.style.display = 'block';
    this.cesiumManager.pauseRendering();

    // Set 2D map to match 3D view
    this.leafletMap.setView([cameraPos.lat, cameraPos.lon], zoom, { animate: false });
    
    // Invalidate map size (important after display change)
    setTimeout(() => {
      this.leafletMap.invalidateSize();
    }, 100);
    
    this.mode = '2d';
    console.log('✅ Switched to 2D mode');
  }
  
  /**
   * Toggle between 2D and 3D
   */
  async toggleView() {
    if (this.mode === '2d') {
      if (this.isMobile()) {
        console.log('🚫 3D mode disabled on mobile. Toggle ignored.');
        return;
      }
      await this.switchTo3D();
    } else {
      this.switchTo2D();
    }
  }
  
  /**
   * Update aircraft in current view
   */
  updateAircraft(position) {
    const { cookie } = position;
    
    // Store position data
    let data = this.aircraftData.get(cookie);
    if (!data) {
      data = {
        position: position,
        trail: [],
        baseColor: null
      };
      this.aircraftData.set(cookie, data);
    }
    
    // If 3D is available, enrich the position (vario_avg/kalman) for labels and colors
    const enriched = (this.aircraftManager3D)
      ? this.aircraftManager3D.preparePosition(cookie, position)
      : position;

    // Detect jumps/seeks and clear trail to prevent long lines
    const MAX_SEGMENT_DISTANCE_KM = 5;
    if (this.config.trailLength > 0 && data.trail.length > 0) {
      const lastPos = data.trail[data.trail.length - 1];
      const distance = this.calculateDistance(
        lastPos.lat, lastPos.lon,
        enriched.lat, enriched.lon
      );
      
      if (distance > MAX_SEGMENT_DISTANCE_KM) {
        console.log(`🎬 3D Trail jump detected: ${distance.toFixed(1)}km - clearing trail for ${cookie}`);
        data.trail = []; // Clear trail on jump
      }
    }
    
    // Update position
    data.position = enriched;

    // Update trail (limit to max points)
    if (this.config.trailLength > 0) {
      data.trail.push(enriched);
      if (data.trail.length > this.config.trailLength) {
        data.trail.shift();
      }
    }
    
    // Get base color from 2D manager if available
    if (!data.baseColor && this.aircraftManager2D) {
      const aircraft2D = this.aircraftManager2D.aircraft.get(cookie);
      if (aircraft2D) {
        data.baseColor = aircraft2D.baseColor;
      }
    }
    
    // Update in active view
    if (this.mode === '2d') {
      // 2D manager handles this directly
      // (already being called in main.js)
    } else if (this.mode === '3d' && this.aircraftManager3D) {
      this.aircraftManager3D.updateAircraft(enriched, data.baseColor);
      
      // Update trail
      if (this.config.trailLength > 0 && data.trail.length > 1) {
        this.trailManager3D.updateTrail(
          cookie,
          data.trail,
          this.config.colorScheme,
          data.baseColor
        );
      }
    }
  }
  
  /**
   * Update only the aircraft marker position (for smooth interpolated movement).
   * Permanent 3D trail geometry stays on the cheaper 1Hz update path; a lightweight
   * pending segment extends the trail head smoothly between authoritative samples.
   * @param {object} position - Position data with lat, lon, heading_deg, cookie
   */
  updateAircraftMarkerOnly(position) {
    // Only update 3D marker if in 3D mode
    if (this.mode === '3d' && this.aircraftManager3D) {
      this.aircraftManager3D.updateAircraftMarkerOnly(position);

      if (this.config.trailLength > 0 && this.trailManager3D) {
        const data = this.aircraftData.get(position.cookie);
        if (data && data.trail.length > 0) {
          const lastPos = data.trail[data.trail.length - 1];
          const pendingPosition = data.position
            ? { ...data.position, ...position }
            : position;
          this.trailManager3D.updatePendingTrail(
            position.cookie,
            lastPos,
            pendingPosition,
            this.config.colorScheme,
            data.baseColor
          );
        } else {
          this.trailManager3D.clearPendingTrail(position.cookie);
        }
      } else if (this.trailManager3D) {
        this.trailManager3D.clearPendingTrail(position.cookie);
      }
    }
    // 2D marker and trail updates are handled by aircraftManager2D.updateAircraftMarkerOnly()
  }

  /**
   * Remove aircraft from current view
   */
  removeAircraft(cookie) {
    this.aircraftData.delete(cookie);
    
    if (this.mode === '3d' && this.aircraftManager3D) {
      this.aircraftManager3D.removeAircraft(cookie);
      this.trailManager3D.removeTrail(cookie);
    }
    // 2D removal is handled by aircraftManager2D directly
  }
  
  /**
   * Clear all trails (used when seeking in replay to prevent warp lines)
   */
  clearAllTrails() {
    // Clear trail data in mapManager's cache
    this.aircraftData.forEach((data) => {
      data.trail = [];
    });

    // Clear 2D trail visuals
    if (this.aircraftManager2D && typeof this.aircraftManager2D.clearAllTrails === 'function') {
      this.aircraftManager2D.clearAllTrails();
    }

    // Clear 3D trail visuals
    if (this.trailManager3D) {
      this.trailManager3D.clearAll();
    }
  }
  
  /**
   * Sync all aircraft from 2D to 3D
   */
  syncAircraftTo3D() {
    if (!this.aircraftManager3D) return;

    console.log(`🔄 Syncing ${this.aircraftData.size} aircraft to 3D with colorScheme: ${this.config.colorScheme}`);

    // Ensure color scheme and sensitivity are applied before syncing
    this.aircraftManager3D.setColorScheme(this.config.colorScheme);
    this.aircraftManager3D.setColorSensitivity(this.config.colorSensitivity);
    this.aircraftManager3D.setTrendColorScheme(this.config.trendColorScheme);
    this.aircraftManager3D.setInfoLabel(this.config.infoLabel);
    this.aircraftManager3D.setUnits(this.config.units);

    // Sync hidden state from 2D to 3D BEFORE creating entities/trails
    // This ensures the hidden state is available when trails check visibility
    if (this.aircraftManager2D && this.aircraftManager2D.hiddenCookies) {
      this.setHiddenCookies(this.aircraftManager2D.hiddenCookies);
    }

    this.aircraftData.forEach((data, cookie) => {
      this.aircraftManager3D.updateAircraft(data.position, data.baseColor);

      if (this.config.trailLength > 0 && data.trail.length > 1) {
        this.trailManager3D.updateTrail(
          cookie,
          data.trail,
          this.config.colorScheme,
          data.baseColor
        );
      }
    });

    // Apply hidden state again AFTER creating entities/trails to ensure
    // any entities created during sync also respect hidden state
    if (this.aircraftManager2D && this.aircraftManager2D.hiddenCookies) {
      this.setHiddenCookies(this.aircraftManager2D.hiddenCookies);
    }
  }
  
  /**
   * Update configuration
   */
  updateConfig(config) {
    // Handle 'colors' as alias for 'colorScheme' (main.js uses 'colors')
    if (config.colors !== undefined && config.colorScheme === undefined) {
      config.colorScheme = config.colors;
    }

    this.config = { ...this.config, ...config };

    // Apply to 3D if initialized
    if (this.aircraftManager3D) {
      if (config.colorScheme !== undefined) {
        this.aircraftManager3D.setColorScheme(config.colorScheme);
        // Redraw all trails with new color scheme
        if (this.mode === '3d') {
          this.redrawAllTrails();
        }
      }
      if (config.colorSensitivity !== undefined) {
        this.aircraftManager3D.setColorSensitivity(config.colorSensitivity);
        // Redraw all trails with new sensitivity
        if (this.mode === '3d') {
          this.redrawAllTrails();
        }
      }
      if (config.trendColorScheme !== undefined) {
        this.aircraftManager3D.setTrendColorScheme(config.trendColorScheme);
        if (this.mode === '3d') {
          this.redrawAllTrails();
        }
      }
      if (config.infoLabel !== undefined) {
        this.aircraftManager3D.setInfoLabel(config.infoLabel);
      }
      if (config.units !== undefined) {
        this.aircraftManager3D.setUnits(config.units);
      }
      if (config.teGain !== undefined || config.teAccelLimit !== undefined) {
        this.aircraftManager3D.setTEParams(
          config.teGain !== undefined ? config.teGain : this.config.teGain,
          config.teAccelLimit !== undefined ? config.teAccelLimit : this.config.teAccelLimit
        );
      }
      if (config.trailLength !== undefined) {
        this.trailManager3D.setMaxPoints(config.trailLength);
        // Redraw trails with new length
        if (this.mode === '3d') {
          this.redrawAllTrails();
        }
      }
    }

    // Always apply to 2D manager
    if (this.aircraftManager2D && (config.teGain !== undefined || config.teAccelLimit !== undefined)) {
      this.aircraftManager2D.setTEParams(
        config.teGain !== undefined ? config.teGain : this.config.teGain,
        config.teAccelLimit !== undefined ? config.teAccelLimit : this.config.teAccelLimit
      );
    }
  }

  /**
   * Redraw all trails with current color scheme (3D only)
   */
  redrawAllTrails() {
    if (!this.trailManager3D || this.mode !== '3d') return;

    this.aircraftData.forEach((data, cookie) => {
      if (this.config.trailLength > 0 && data.trail.length > 1) {
        this.trailManager3D.updateTrail(
          cookie,
          data.trail,
          this.config.colorScheme,
          data.baseColor
        );
      }
    });
  }
  
  /**
   * Convert Leaflet zoom to Cesium camera height
   */
  zoomToHeight(zoom) {
    // Approximate conversion: height = 40075000 * cos(lat) / 2^(zoom+1)
    // Simplified for mid-latitudes
    return 40075000 / Math.pow(2, zoom + 1);
  }
  
  /**
   * Convert Cesium camera height to Leaflet zoom
   */
  heightToZoom(height) {
    // Inverse of zoomToHeight
    const zoom = Math.log2(40075000 / height) - 1;
    return Math.max(1, Math.min(18, Math.round(zoom)));
  }
  
  /**
   * Get current mode
   */
  getMode() {
    return this.mode;
  }
  
  /**
   * Check if 3D is initialized
   */
  is3DInitialized() {
    return this.cesiumManager !== null;
  }
  
  /**
   * Draw a task in the current view
   * @param {Object} task - Task object with turnpoints
   */
  drawTask(task) {
    if (this.mode === '3d' && this.taskManager3D) {
      this.taskManager3D.drawTask(task);
    }
    // 2D task drawing is handled by main.js drawTaskOnMap function
  }
  
  /**
   * Clear the current task from the current view
   */
  clearTask() {
    if (this.mode === '3d' && this.taskManager3D) {
      this.taskManager3D.clearTask();
    }
    // 2D task clearing is handled by main.js clearTaskOverlay function
  }
  
  /**
   * Calculate distance between two coordinates in kilometers
   * Uses Haversine formula
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }
  
  /**
   * Sync task state when switching views
   * @param {Object} task - Current task object (or null if no task)
   */
  syncTask(task) {
    if (this.mode === '3d' && this.taskManager3D) {
      if (task) {
        this.taskManager3D.drawTask(task);
      } else {
        this.taskManager3D.clearTask();
      }
    }
  }
  
  /**
   * Set CN label size (3D only)
   * @param {number} size - Font size in pixels (12-24)
   */
  setCNLabelSize(size) {
    if (this.aircraftManager3D) {
      this.aircraftManager3D.setCNLabelSize(size);
    }
  }

  /**
   * Set hidden cookies and apply visibility to 3D aircraft and trails
   * @param {Set} hiddenSet - Set of cookies that should be hidden
   */
  setHiddenCookies(hiddenSet) {
    if (this.aircraftManager3D) {
      this.aircraftManager3D.setHiddenCookies(hiddenSet);
    }
    if (this.trailManager3D) {
      this.trailManager3D.setHiddenCookies(hiddenSet);
    }
  }
}
